/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/require-default-props */
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '@saleshandy/design-system';
import { Tag } from '@saleshandy/icons';
import { RootState } from '../../../../store/root-reducer';
import SHTag from '../../../../shared/components/images/sh-tag';
import Modal from '../../../../shared/design-system/components/atoms/modal';
import TagAutosuggest from '../../../../shared/design-system/components/molecules/tag-autosuggest';
import { RenderMode } from '../../../../shared/design-system/components/molecules/tag-autosuggest/tag-autosuggest';
import {
  AddTagsToLeadsPayload,
  BulkActionPayload,
  BulkActionRemoveTagsPayload,
  SavedTabFilters,
  FilterComponentType,
  LeadFinderCompanyTagAction,
  LeadFinderListTab,
} from '../../types';
import {
  addTagsToBulkLeads,
  addTagsToLeads,
  removeTagsFromBulkLeads,
  removeTagsToLeads,
  companyTagActionAPI,
  companyBulkTagActionAPI,
} from '../../helpers/leads.api';
import toaster, { Theme } from '../../../../shared/toaster';
import { accessibleOnClick } from '../../../../shared/utils';

interface V2AddRemoveTagButtonProps {
  mode?: LeadFinderListTab;
  handleAddTag: () => void;
  handleRemoveTag: () => void;
  disabled?: boolean;
  leadTags: any[];
  /** People mode */
  selectedLeads?: number[];
  selectedLeadObjects?: any[];
  deselectedLeads?: number[];
  selectedAllLeads?: boolean;
  setSelectedLeads?: React.Dispatch<React.SetStateAction<number[]>>;
  selectedCompanies?: number[];
  selectedCompanyObjects?: any[];
  selectedAllCompanies?: boolean;
  companyFilter?: Record<string, any>;
  setSelectedCompanies?: React.Dispatch<React.SetStateAction<number[]>>;
  setIsSelectedAllCompanies?: React.Dispatch<React.SetStateAction<boolean>>;
  leadsFilter: SavedTabFilters;
  setSavedFilters: React.Dispatch<React.SetStateAction<SavedTabFilters>>;
  handleQuery: (args: {
    filterKey: string;
    filterValue: any;
    filterType: string;
  }) => void;
  /** When set, opens Add Tag modal for this company/lead (row action) */
  triggerOpenAddTag?: number | null;
  /** When set, opens Remove Tag modal for this company/lead (row action) */
  triggerOpenRemoveTag?: number | null;
  /** Called when modal opened from row action is closed */
  onRowTagModalClosed?: () => void;
  /** Override for selected company objects when opening from row (company may not be in current page) */
  overrideSelectedCompanyObjects?: any[];
  /** Override for selected lead objects when opening from row (people may not be in current page) */
  overrideSelectedLeadObjects?: any[];
  /** Called after a successful tag removal to refresh the saved count */
  onRefreshSavedCount?: () => Promise<void>;
}

const V2AddRemoveTagButton: React.FC<V2AddRemoveTagButtonProps> = ({
  mode = LeadFinderListTab.People,
  handleAddTag,
  handleRemoveTag,
  disabled = false,
  leadTags,
  selectedLeads = [],
  selectedLeadObjects = [],
  deselectedLeads = [],
  selectedAllLeads = false,
  setSelectedLeads,
  selectedCompanies = [],
  selectedCompanyObjects = [],
  selectedAllCompanies = false,
  companyFilter = {},
  setSelectedCompanies,
  setIsSelectedAllCompanies,
  leadsFilter,
  setSavedFilters,
  handleQuery,
  triggerOpenAddTag,
  triggerOpenRemoveTag,
  onRowTagModalClosed,
  overrideSelectedCompanyObjects,
  overrideSelectedLeadObjects,
  onRefreshSavedCount,
}) => {
  const { t } = useTranslation();
  // User-applied caps on how many records a select-all tag action processes.
  const peopleMaxLeadsLimit = useSelector(
    (state: RootState) => state.leadFinderV2.selection.peopleMaxLeadsLimit,
  );
  const companyMaxLeadsLimit = useSelector(
    (state: RootState) => state.leadFinderV2.selection.companyMaxLeadsLimit,
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [tagsToAdd, setTagsToAdd] = useState<any[]>([]);
  const [tagsToRemove, setTagsToRemove] = useState<any[]>([]);
  const [isRemoveMode, setIsRemoveMode] = useState(false);
  const [isApplyButtonDisabled, setIsApplyButtonDisabled] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isTagInputOverLimit, setIsTagInputOverLimit] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => setShowDropdown(!showDropdown);
  const closeDropdown = () => setShowDropdown(false);
  const closeModal = () => setShowModal(false);

  const isCompanyMode = mode === LeadFinderListTab.Company;
  const rowTriggerId = triggerOpenAddTag ?? triggerOpenRemoveTag ?? null;
  const selectedIds = isCompanyMode ? selectedCompanies : selectedLeads;
  const selectedObjects = isCompanyMode
    ? selectedCompanyObjects
    : selectedLeadObjects;
  const effectiveSelectedIds =
    rowTriggerId != null ? [rowTriggerId] : selectedIds;
  const effectiveSelectedObjects = (() => {
    if (rowTriggerId == null) return selectedObjects;
    if (isCompanyMode) {
      if (overrideSelectedCompanyObjects?.length) {
        return overrideSelectedCompanyObjects;
      }
      return selectedCompanyObjects.filter(
        (c: any) => Number(c?.id ?? c?.company_id) === rowTriggerId,
      );
    }
    // People mode - allow overriding selected lead object when opening modal from a row
    if (overrideSelectedLeadObjects?.length) return overrideSelectedLeadObjects;
    return selectedLeadObjects.filter(
      (l: any) => Number(l?.id) === rowTriggerId,
    );
  })();
  const setSelectedIds = isCompanyMode
    ? setSelectedCompanies
    : setSelectedLeads;
  const tagAutosuggestKey = effectiveSelectedObjects
    .map((item: any) => {
      const itemId = item?.id ?? item?.company_id ?? '';
      const tagIds = Array.isArray(item?.tags)
        ? item.tags
            .map((tag: any) => {
              const tagId = tag?.id ?? tag;
              return tagId != null ? `${tagId}:${tag?.name ?? ''}` : null;
            })
            .filter(Boolean)
            .join(',')
        : '';

      return `${itemId}:${tagIds}`;
    })
    .join('|');

  const closeModalWithCallback = () => {
    closeModal();
    if (rowTriggerId != null) {
      onRowTagModalClosed?.();
    }
  };

  const openAddTagModal = () => {
    closeDropdown();
    setModalTitle('Add Tags');
    setIsRemoveMode(false);
    setShowModal(true);
    setTagsToAdd([]);
    setIsApplyButtonDisabled(true);
    setIsTagInputOverLimit(false);
  };

  const openRemoveTagModal = () => {
    closeDropdown();
    setModalTitle('Remove Tags');
    setIsRemoveMode(true);
    setShowModal(true);
    setTagsToRemove([]);
    setTagsToAdd([]);
    setIsApplyButtonDisabled(false);
  };

  useEffect(() => {
    if (triggerOpenAddTag != null) {
      openAddTagModal();
    }
  }, [triggerOpenAddTag]);

  useEffect(() => {
    if (triggerOpenRemoveTag != null) {
      openRemoveTagModal();
    }
  }, [triggerOpenRemoveTag]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    // Add event listener to detect clicks outside the dropdown
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isRemoveMode) {
      const newTagValue = tagsToAdd.some((tag) => tag.name.length > 64);
      setIsApplyButtonDisabled(
        tagsToAdd.length === 0 || newTagValue || isTagInputOverLimit,
      );
    }
  }, [tagsToAdd, isRemoveMode, isTagInputOverLimit]);

  useEffect(() => {
    if (isRemoveMode) {
      setIsApplyButtonDisabled(!(tagsToRemove && tagsToRemove.length > 0));
    }
  }, [tagsToRemove, isRemoveMode]);

  const handleSubmitModal = async () => {
    let didSucceed = false;
    try {
      setIsSubmitLoading(true);
      const isRowAction = rowTriggerId != null;

      if (isCompanyMode) {
        if (effectiveSelectedIds.length === 0 && !selectedAllCompanies) {
          toaster.error('No company selected for tag update', {
            theme: Theme.New,
          });
          return;
        }

        if (!isRowAction && selectedAllCompanies) {
          if (!isRemoveMode) {
            const tagIds: number[] = [];
            const newTags: string[] = [];
            tagsToAdd.forEach((tag) => {
              if (tag.isNew) {
                newTags.push(tag.name);
              } else {
                tagIds.push(tag.id);
              }
            });
            const response = await companyBulkTagActionAPI({
              actionType: LeadFinderCompanyTagAction.AddTag,
              ...(tagIds.length > 0 && { tagIds }),
              ...(newTags.length > 0 && { newTags }),
              // ...(companyMaxLeadsLimit != null && {
              //   maxLeadsToBeProcessed: companyMaxLeadsLimit,
              // }),
              companyFilter,
            });
            const toastMessage = response?.message
              ? response.message
              : 'Your bulk process has been initiated. This may take a few moments to reflect.';
            toaster.success(toastMessage, { theme: Theme.New });
          } else {
            const tagIds = tagsToRemove.map((tag) => tag.id || tag);
            const response = await companyBulkTagActionAPI({
              actionType: LeadFinderCompanyTagAction.RemoveTag,
              tagIds,
              // ...(companyMaxLeadsLimit != null && {
              //   maxLeadsToBeProcessed: companyMaxLeadsLimit,
              // }),
              companyFilter,
            });
            const toastMessage = response?.message
              ? response.message
              : 'Your bulk process has been initiated. This may take a few moments to reflect.';
            toaster.success(toastMessage, { theme: Theme.New });
          }
        } else if (!isRemoveMode) {
          const tagIds: number[] = [];
          const newTags: string[] = [];
          tagsToAdd.forEach((tag) => {
            if (tag.isNew) {
              newTags.push(tag.name);
            } else {
              tagIds.push(tag.id);
            }
          });
          await companyTagActionAPI({
            actionType: LeadFinderCompanyTagAction.AddTag,
            companyIds: effectiveSelectedIds,
            tagIds,
            newTags,
          });
          toaster.success('Tag applied successfully', { theme: Theme.New });
        } else {
          const tagIds = tagsToRemove.map((tag) => tag.id || tag);
          await companyTagActionAPI({
            actionType: LeadFinderCompanyTagAction.RemoveTag,
            companyIds: effectiveSelectedIds,
            tagIds,
          });
          toaster.success('Tags removed successfully', { theme: Theme.New });
        }

        // Refresh the table in the background so the modal closes immediately
        Promise.resolve(
          isRemoveMode ? handleRemoveTag() : handleAddTag(),
        ).catch(() => {
          toaster.error(
            isRemoveMode ? 'Failed to remove tags' : 'Failed to add tags',
            { theme: Theme.New },
          );
          // Ignore background refresh errors; the tag change already succeeded.
        });
        // setSavedFilters(handleDefaultValues());
        setSelectedIds?.([]);
        if (selectedAllCompanies) {
          setIsSelectedAllCompanies?.(false);
        }
      } else {
        const payload: AddTagsToLeadsPayload = {
          leads: effectiveSelectedIds,
          tagIds: [],
          newTags: [],
        };

        if (!isRemoveMode) {
          const bulkPayload: BulkActionPayload = {
            deSelectedLeadIds: deselectedLeads,
            ...(peopleMaxLeadsLimit != null && {
              maxLeadsToBeProcessed: peopleMaxLeadsLimit,
            }),
            leadsFilter: {
              ...leadsFilter,
              listIds:
                leadsFilter?.listIds?.includes?.length > 0 &&
                leadsFilter.listIds.includes,
            },
            newTags: [],
            tagIds: [],
          };
          tagsToAdd.forEach((tag) => {
            if (tag.isNew) {
              payload.newTags.push(tag.name);
              bulkPayload.newTags?.push(tag.name);
            } else {
              payload.tagIds.push(tag.id);
              bulkPayload.tagIds?.push(tag.id);
            }
          });

          let didAddTags = false;
          if (!isRowAction && selectedAllLeads) {
            const response = await addTagsToBulkLeads(bulkPayload);
            const toastMessage = response?.message
              ? response.message
              : 'Tags are being assigned to the selected leads. This may take a few moments to reflect.';
            toaster.success(toastMessage, {
              theme: Theme.New,
            });
            didAddTags = true;
          } else if (payload?.leads?.length > 0) {
            await addTagsToLeads(payload);
            toaster.success('Tag applied successfully', { theme: Theme.New });
            didAddTags = true;
          }

          if (didAddTags) {
            // Background refresh — don't block the modal close on the re-fetch.
            Promise.resolve(handleAddTag()).catch(() => {
              toaster.error(
                isRemoveMode ? 'Failed to remove tags' : 'Failed to add tags',
                { theme: Theme.New },
              );
              // Ignore background refresh errors; the tag add already succeeded.
            });
          }
          // setSavedFilters(handleDefaultValues());
          setSelectedIds?.([]);
        } else {
          const bulkPayload: BulkActionRemoveTagsPayload = {
            deSelectedLeadIds: deselectedLeads,
            ...(peopleMaxLeadsLimit != null && {
              maxLeadsToBeProcessed: peopleMaxLeadsLimit,
            }),
            leadsFilter: {
              ...leadsFilter,
              listIds:
                leadsFilter?.listIds?.includes?.length > 0 &&
                leadsFilter.listIds.includes,
            },
            tagIds: [],
          };
          tagsToRemove.forEach((tag) => {
            payload.tagIds.push(tag.id || tag);
            bulkPayload.tagIds?.push(tag.id || tag);
          });

          let didRemoveTags = false;
          if (!isRowAction && selectedAllLeads) {
            const response = await removeTagsFromBulkLeads(bulkPayload);
            const toastMessage = response?.message
              ? response.message
              : 'Tags are being un-assigned from the selected leads. This may take a few moments to reflect.';
            toaster.success(toastMessage, { theme: Theme.New });
            didRemoveTags = true;
          } else if (payload?.leads?.length > 0) {
            await removeTagsToLeads({
              tagIds: payload.tagIds,
              leads: payload.leads,
            });

            toaster.success('Tags removed successfully', {
              theme: Theme.New,
            });
            didRemoveTags = true;
          }

          // setSavedFilters(handleDefaultValues());
          if (didRemoveTags) {
            // Background refresh — don't block the modal close on the re-fetch.
            Promise.resolve(handleRemoveTag())
              .then(() => onRefreshSavedCount?.())
              .catch(() => {
                // Ignore background refresh errors; the removal already succeeded.
                toaster.error(
                  isRemoveMode ? 'Failed to remove tags' : 'Failed to add tags',
                  { theme: Theme.New },
                );
              });
          }
          // const currentTags = Array?.isArray(leadsFilter?.tags)
          //   ? leadsFilter?.tags
          //   : [];
          // const tagsToRemoveSet = new Set(
          //   tagsToRemove.map((tag) => tag.id || tag),
          // );
          // handleQuery({
          //   filterKey: 'tags',
          //   filterValue: { tags: updatedTags },
          //   filterType: FilterComponentType.TAG_CHECKBOX,
          // });
          setSelectedIds?.([]);
        }
      }
      didSucceed = true;
    } catch (error: any) {
      toaster.error(
        isRemoveMode ? 'Failed to remove tags' : 'Failed to add tags',
        error,
      );
    } finally {
      setIsSubmitLoading(false);
      if (didSucceed) {
        closeModalWithCallback();
      }
    }
  };

  const modalLabel = isRemoveMode ? 'labels.remove' : 'labels.apply';

  return (
    <>
      <div className="d-flex align-items-center pointer position-relative">
        <Button
          variant="secondary"
          disabled={disabled}
          className="btn-custom-secondary"
          onClick={toggleDropdown}
        >
          <Tag />
          <span>Tags</span>
        </Button>
        {showDropdown && (
          <div className="tag-dropdown-view" ref={dropdownRef}>
            <ul className="dropdown-container">
              <li className="menu-item">
                <div
                  className="d-flex px-2 export-cursor-pointer dropdown"
                  {...accessibleOnClick(openAddTagModal)}
                >
                  <div className="p-text-tag">
                    <p className="tag-list-font menu-text">Add Tags</p>
                  </div>
                </div>
              </li>
              <li className="menu-item">
                <div
                  className="d-flex px-2 export-cursor-pointer dropdown"
                  {...accessibleOnClick(openRemoveTagModal)}
                >
                  <div className="p-text-tag remove-tag-modal">
                    <p className="tag-list-font menu-text">Remove Tags</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        )}
      </div>

      <Modal
        show={showModal}
        className="add-remove-tag-modal"
        titleContent={
          <div className="d-flex modal-header-title">
            <SHTag />
            <div className="pl-2">{modalTitle}</div>
          </div>
        }
        onClose={closeModalWithCallback}
        showCloseIcon={false}
        submitButtonText={t(`${modalLabel}`)}
        isSubmitDisabled={isApplyButtonDisabled || isSubmitLoading}
        isSubmitLoading={isSubmitLoading}
        isCancelDisabled={isSubmitLoading}
        onSubmit={handleSubmitModal}
        backdrop="static"
        extraModalProps={{
          enforceFocus: false,
          'aria-labelledby': 'contained-modal-title-vcenter',
          keyboard: !isSubmitLoading,
        }}
      >
        <div
          className={`overflow-hidden saved-tab-tags ${
            isRemoveMode && 'remove-tag-modal'
          }`}
        >
          <TagAutosuggest
            key={`${isRemoveMode ? 'remove' : 'add'}-${tagAutosuggestKey}`}
            selectedProspects={effectiveSelectedObjects}
            tags={leadTags}
            tagsToAdd={tagsToAdd}
            setTagsToAdd={setTagsToAdd}
            tagsToRemove={tagsToRemove}
            setTagsToRemove={setTagsToRemove}
            renderMode={RenderMode.Inline}
            showAddedTags={isRemoveMode}
            placeholder={
              isRemoveMode
                ? t('messages.tags_removeMode')
                : t('messages.tags_placeholder')
            }
            onCharLimitWarning={
              !isRemoveMode ? setIsTagInputOverLimit : undefined
            }
          />
        </div>
      </Modal>
    </>
  );
};

// Memoize to prevent unnecessary re-renders
export default React.memo(V2AddRemoveTagButton);
